import {
    SpaceBetween,
    BreadcrumbGroup,
    Header,
    Box,
    Table,
    Button,
    TextFilter,
    Pagination,
    CollectionPreferences,
    Flashbar,
    Link,
} from '@amzn/awsui-components-console';
import React, { useEffect, useState } from 'react';
import * as Constants from './constants';
import { listAllBots, offboardBot } from './apis';
import { EmptyState, getFilterCounterText } from '../../lab-management/group/utils';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { useHistory } from 'react-router-dom';
import { displayErrorContent, displaySuccessContent } from '../../lib/utils';
import { SERVER_ERROR } from '../../lib/constants';

export default function BotAccounts() {
    /**
     * Bot account component
     */
    const history = useHistory();
    const [selectedBot, setSelectedBot] = React.useState([]);
    const [tableLoading, setTableLoading] = React.useState(false);
    const [deleteLoading, setDeleteLoading] = React.useState(false);
    const [bots, setBots] = React.useState([]);
    const [deleteDisabled, setDeleteDisabled] = React.useState(true);
    const [configureDisabled, setConfigureDisabled] = React.useState(true);
    const [successMessage, setSuccessMessage] = React.useState([]);
    const [errorMessage, setErrorMessage] = React.useState([]);
    const [preferences, setPreferences] = useState({
        pageSize: 20,
        stickyColumns: { first: 1 },
        visibleContent: Constants.BOT_VISIBLE_COLUMNS,
    });
    const { items, actions, collectionProps, filteredItemsCount, filterProps, paginationProps } = useCollection(bots, {
        filtering: {
            noMatch: (
                <EmptyState
                    title="No matches"
                    subtitle="We can’t find a match."
                    action={<Button onClick={() => actions.setFiltering('')}>Clear filter</Button>}
                />
            ),
        },
        pagination: { pageSize: preferences.pageSize },
        sorting: {
            defaultState: { sortingColumn: Constants.SORTED_FILED, isDescending: true },
        },
        selection: {},
    });

    /**
     * process selection change event
     */
    const onSelectionChange = (selectedItems) => {
        // when bot is selected
        setSelectedBot(selectedItems);
        setDeleteDisabled(false);
        setConfigureDisabled(false);
    };
    const startLoading = () => {
        // initialize loading status
        setTableLoading(true);
        setDeleteDisabled(true);
        setConfigureDisabled(true);
    };
    const endLoading = () => {
        // end loading status
        setTableLoading(false);
    };

    /**
     * load bot table
     */
    const loadTable = () => {
        (async function () {
            startLoading();
            try {
                const response = await listAllBots();
                const bots = response['response'];
                setBots(bots);
            } catch (e) {
                console.error('Fail to load bot(s) with error', e);
            }
            endLoading();
        })();
    };

    /**
     * process delete button click event
     */
    const onSubmitDelete = () => {
        (async function () {
            setDeleteLoading(true);
            try {
                const username = selectedBot[0]['username'];
                await offboardBot(username);
                loadTable();
                displaySuccessContent(
                    `Successfully offboard the selected bot account ${username} from DS2 Device Farm`,
                    setSuccessMessage
                );
            } catch (e) {
                console.error('Fail to offboard the selected bot account with error', e);
                displayErrorContent(
                    SERVER_ERROR,
                    'Fail to offboard the selected bot account from DS2 Device Farm. Contact our dev team @ds2-device-farm-dev for more details.',
                    setErrorMessage
                );
            }
            setDeleteLoading(false);
        })();
    };

    /**
     * process configure button click event
     */
    const onConfigureClick = () => {
        if (selectedBot.length === 0) {
            console.error('when no bot is selected, configure button should not be enabled.');
        } else {
            const bot = selectedBot[0];
            history.push({
                pathname: `/api-auth/bot-accounts/configure/${bot['username']}`,
                state: bot,
            });
        }
    };

    useEffect(() => {
        // initialize the component
        loadTable();
    }, []);

    return (
        <SpaceBetween size="s">
            <BreadcrumbGroup
                items={[
                    { text: 'DS2 Device Farm', href: '#' },
                    { text: 'Bot accounts', href: '#/api-auth/bot-accounts' },
                ]}
                ariaLabel="Breadcrumbs"
            />
            <Header
                variant="h1"
                description={
                    <>
                        Bot accounts are specialized shared accounts that enable programmatic access to DS2 Device Farm
                        for automated tasks and integrations. These accounts are designed for team use rather than
                        individual access. For a comprehensive, step-by-step guide on how to set up and onboard a bot
                        account, please consult our{' '}
                        <Link
                            external
                            href="https://w.amazon.com/bin/view/DS2DeviceFarm/Documents/ReadMe/BotAccountsOnboarding/"
                        >
                            wiki
                        </Link>
                        .
                    </>
                }
            >
                Bot accounts
            </Header>
            <Table
                {...collectionProps}
                onSelectionChange={({ detail }) => onSelectionChange(detail.selectedItems)}
                selectedItems={selectedBot}
                selectionType="single"
                header={
                    <Header
                        counter={`(${bots.length})`}
                        actions={
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button onClick={loadTable} iconName="refresh" variant="icon" />
                                <Button disabled={deleteDisabled} onClick={onSubmitDelete} loading={deleteLoading}>
                                    Offboard
                                </Button>
                                <Button disabled={configureDisabled} onClick={onConfigureClick}>
                                    Configure
                                </Button>
                                <Button href="#/api-auth/bot-accounts/onboard" variant="primary">
                                    Onboard
                                </Button>
                            </SpaceBetween>
                        }
                    >
                        DDF Bot accounts
                    </Header>
                }
                visibleColumns={preferences.visibleContent}
                columnDefinitions={Constants.BOT_COLUMNS_DEFINITION}
                items={items}
                pagination={<Pagination {...paginationProps} />}
                filter={<TextFilter {...filterProps} countText={getFilterCounterText(filteredItemsCount)} />}
                empty={
                    <Box textAlign="center" color="inherit">
                        <b>No accounts to display</b>
                    </Box>
                }
                loading={tableLoading}
                loadingText="Loading bots"
                stickyColumns={preferences.stickyColumns}
                resizableColumns
                preferences={
                    <CollectionPreferences
                        {...Constants.COLLECTION_PREFERENCES_PROPS}
                        preferences={preferences}
                        onConfirm={({ detail }) => setPreferences(detail)}
                    />
                }
            />
            <Flashbar items={errorMessage} />
            <Flashbar items={successMessage} />
        </SpaceBetween>
    );
}
